import { Alert, Button, ChipMetadata, Selector, Tooltip } from '@pmi/dsm-react';
import { MoreVerticalOutlineIcon } from '@pmi/dsm-react/dist/components/icons';
import {
  PlusIcon,
  TriangleAlertIcon,
} from '@pmi/dsm-react/dist/components/icons/16';
import { AlertErrorIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';
import { useEffect, useMemo, useState } from 'react';
import RenderHtml from '../../../../../../shared/components/RenderHtml';
import ResponsiveTable from '../../../../../../shared/components/ResponsiveTable';
import { SortedColumn } from '../../../../../../shared/components/ResponsiveTable/models';
import { toDayMonthYear } from '../../../../../../shared/helpers';
import usePermissions from '../../../../../../shared/hooks/usePermissions';
import { AgreementApiModel } from '../../../../../../shared/models/DealModel';
import { ProductApiModel } from '../../../../../../shared/models/ProductModel';
import { User } from '../../../../../../shared/models/UserModels';
import PERMISSIONS from '../../../../../../shared/permissions';
import { printableZonesLabel } from '../../../../../../shared/utils/printableZoneRestrictionLabel';
import styles from '../../UsersList/userslist.module.scss';
import { UserProduct } from './apiSlice';
import DiagnosticsModal from './DiagnosticsModal';
import ReassignProducts from './ReassignProducts';
import RemoveProductModal from './RemoveProductModal';
import UnErrollProductModal from './UnErrollProductModal';
import userProductStyles from './userCurrentProductsTable.module.scss';

export interface UserCurrentProductsTableProps {
  list: UserProduct[];
  isLoading: boolean;
  accessibleAgreements: AgreementApiModel[];
  user: User;
  onAddProduct: (user: User) => void;
}

interface UserCurrentProductActionButtonEvents {
  onRemove: (
    userProductId: number,
    accountProductId: number,
    agreementId: string | null,
  ) => void;
  onReassign: (
    userProductId: number,
    accountProductId: number,
    agreementId: string | null,
  ) => void;
  onUnEnroll: (
    userProductId: number,
    accountProductId: number,
    agreementId: string | null,
  ) => void;
  onDiagnose: (
    userProductId: number,
    accountProductId: number,
    agreementId: string | null,
  ) => void;
}

export const getColumns = (
  isDealAdmin: boolean,
  isInternalAdmin: boolean,
  canUnassignProducts: boolean,
  actions: UserCurrentProductActionButtonEvents,
  isUserDisabled: boolean,
  hasRestrictedProduct: boolean,
  isUserPending: boolean,
  restrictedAgreementIds: Set<string>,
): SortedColumn[] => [
  {
    name: 'productName',
    title: 'Product',
    cell: (cntx) => (
      <span className="px-1 d-block">
        <Tooltip
          content={
            <RenderHtml html={cntx.row.original?.productName} tag="span" />
          }
          type="sm"
          placement="left"
          blockTriggers={cntx.row.original?.productName?.length < 33}
          className="darkTooltip"
        >
          <RenderHtml
            className={styles.productNameEllipsis}
            html={cntx.row.original?.productName}
            tag="span"
          />
        </Tooltip>
      </span>
    ),
    width: 25,
    sortingEnabled: true,
  },
  {
    name: 'assignedOn',
    title: 'Assigned',
    cell: (cntx) => (
      <span className="px-1 d-block">
        {cntx.row.original?.assignedDate
          ? toDayMonthYear(cntx.row.original?.assignedDate)
          : 'N/A'}
      </span>
    ),
    width: 15,
    sortingEnabled: true,
  },
  {
    name: 'enrollmentStatus',
    title: 'Redeemed',
    className: hasRestrictedProduct ? 'text-danger' : undefined,
    cell: (cntx) => {
      const status = cntx.row.original?.enrollmentStatus ?? '';
      const enrollmentDate = cntx.row.original?.enrollmentDate;
      const statusLabel = () => {
        if (status.toLowerCase() === 'redeemed') {
          if (enrollmentDate?.startsWith('0001-01-01T00:00:00')) {
            return '01 Jan 0001';
          }
          return enrollmentDate ? toDayMonthYear(enrollmentDate) : 'Redeemed';
        }
        if (status.toLowerCase() === 'restricted') {
          return status;
        }
        return enrollmentDate ? toDayMonthYear(enrollmentDate) : 'Not Redeemed';
      };
      if (status?.length === 0) {
        return 'N/A';
      }
      return (
        <span className="px-1 d-block">
          <span
            className={`d-inline ${
              status != null && status.toLowerCase() === 'restricted'
                ? 'text-danger'
                : ''
            }`}
          >
            {!isUserPending && <span>{statusLabel()}</span>}
            {isUserPending && 'Pending'}
          </span>
        </span>
      );
    },
    width: 15,
    sortingEnabled: true,
  },
  {
    name: 'enrollmentProviderIncrementId',
    title: 'Order ID',
    cell: (cntx) => (
      <span className="px-1 d-block">
        {cntx.row.original?.enrollmentProviderIncrementId || 'N/A'}
      </span>
    ),
    width: 15,
    sortingEnabled: true,
  },
  {
    name: 'examDate',
    title: 'Exam Date',
    cell: (cntx) => (
      <span className="px-1 d-block">
        {cntx.row.original?.enrollmentStatus?.toLowerCase() !== 'restricted' &&
          (cntx.row.original?.eventDate?.startsWith('0001-01-01T00:00:00')
            ? '01 Jan 0001'
            : toDayMonthYear(cntx.row.original?.eventDate) || 'N/A')}
        {cntx.row.original?.enrollmentStatus?.toLowerCase() === 'restricted' &&
          'Not Scheduled'}
      </span>
    ),
    width: 15,
    sortingEnabled: true,
  },
  {
    name: 'agreementName',
    title: 'Agreement ID',
    cell: (cntx) => (
      <span className="px-1 d-block">
        {cntx.row.original?.agreementName.length > 0 ? (
          <span className="d-block">
            <span className={`${userProductStyles.textLinkOnLight} d-block`}>
              {cntx.row.original?.agreementName}
            </span>
            <small className="text-muted d-block">
              {cntx.row.original?.agreementId}
            </small>
          </span>
        ) : (
          <span className={`${userProductStyles.textLinkOnLight} d-block`}>
            {cntx.row.original?.agreementId}
          </span>
        )}
      </span>
    ),
    width: 15,
    sortingEnabled: true,
  },
  {
    name: 'zone',
    title: 'Zone',
    cell: (cntx) => {
      const zones = cntx?.row.original?.zones;
      if (zones.length > 0) {
        return (
          <span className="px-1 d-block">
            {printableZonesLabel(
              Array.isArray(zones)
                ? zones.map((z: { value: string }) => z.value)
                : zones,
            ).replace('Countries in ', '')}
          </span>
        );
      }
      return <span className="px-1">All Zones</span>;
    },
    width: 10,
    sortingEnabled: true,
  },
  {
    name: 'actions',
    title: 'Actions',
    cell: (cntx) => {
      const status = cntx.row.original?.enrollmentStatus ?? '';
      const isSelfService = cntx.row.original?.isSelfServiceProduct ?? false;
      return (
        <Tooltip
          content={<RenderHtml html="User is currently disabled" tag="span" />}
          type="sm"
          placement="left"
          blockTriggers={!isUserDisabled}
          className="darkTooltip"
        >
          <Selector
            label={<MoreVerticalOutlineIcon fontSize="sm" color="disabled" />}
            className={userProductStyles.actionButtonMenuButtonContainer}
            placement="left"
            align="end"
            disabled={isUserDisabled}
          >
            <div className="d-block p-2">
              <ul
                className={`list-unstyled ${userProductStyles.actionButtonMenuButton}`}
              >
                <li data-agreement={cntx.row.original?.agreementId}>
                  <Tooltip
                    content="Only allowed for not redeemed products"
                    className="darkTooltip"
                    blockTriggers={
                      !(
                        ((!isDealAdmin || !canUnassignProducts) &&
                          !isInternalAdmin) ||
                        status.toLowerCase() === 'redeemed' ||
                        restrictedAgreementIds.has(
                          cntx.row.original?.agreementId ?? '',
                        )
                      )
                    }
                  >
                    <button
                      type="button"
                      className="btn-sm btn btn-light"
                      onClick={() => {
                        actions.onRemove(
                          cntx.row.original.id,
                          cntx.row.original.accountProductId,
                          cntx.row.original?.agreementId,
                        );
                      }}
                      disabled={
                        status.toLowerCase() === 'redeemed' ||
                        ((!isDealAdmin || !canUnassignProducts) &&
                          !isInternalAdmin) ||
                        restrictedAgreementIds.has(
                          cntx.row.original?.agreementId ?? '',
                        )
                      }
                    >
                      Unassign Product
                    </button>
                  </Tooltip>
                </li>
                {cntx.row.original?.enrollmentStatus != null &&
                  cntx.row.original?.enrollmentStatus.toLowerCase() ===
                    'restricted' && (
                    <li data-agreement={cntx.row.original?.agreementId}>
                      <button
                        type="button"
                        className="btn-sm btn btn-light"
                        onClick={() =>
                          actions.onReassign(
                            cntx.row.original.id,
                            cntx.row.original.accountProductId,
                            cntx.row.original?.agreementId,
                          )
                        }
                        disabled={!isDealAdmin && !isInternalAdmin}
                      >
                        Reassign Product
                      </button>
                    </li>
                  )}
                {isInternalAdmin && (
                  <>
                    <li data-agreement={cntx.row.original?.agreementId}>
                      <Tooltip
                        content="Only allowed for redeemed self-service products"
                        className="darkTooltip"
                        blockTriggers={
                          !(
                            status.toLowerCase() !== 'redeemed' ||
                            !isSelfService
                          )
                        }
                      >
                        <button
                          type="button"
                          className="btn-sm btn btn-light"
                          onClick={() =>
                            actions.onUnEnroll(
                              cntx.row.original.id,
                              cntx.row.original.accountProductId,
                              cntx.row.original?.agreementId,
                            )
                          }
                          disabled={
                            status.toLowerCase() !== 'redeemed' ||
                            !isSelfService
                          }
                        >
                          Unenroll Self-service (PMI Only)
                        </button>
                      </Tooltip>
                    </li>
                    <li data-agreement={cntx.row.original?.agreementId}>
                      <button
                        type="button"
                        className="btn-sm btn btn-light"
                        onClick={() =>
                          actions.onDiagnose(
                            cntx.row.original.id,
                            cntx.row.original.accountProductId,
                            cntx.row.original?.agreementId,
                          )
                        }
                      >
                        Run Diagnostics (PMI Only)
                      </button>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </Selector>
        </Tooltip>
      );
    },
    width: 10,
    sortingEnabled: false,
  },
];

const sortProducts = (
  data: UserProduct[],
  fieldName: string,
  sortOrder: number,
) => {
  const clone = [...data];
  return clone?.sort((a, b) =>
    // eslint-disable-next-line no-nested-ternary
    a[fieldName]?.toString().toLowerCase() ===
    b[fieldName]?.toString().toLowerCase()
      ? 0
      : a[fieldName]?.toString().toLowerCase() >
        b[fieldName]?.toString().toLowerCase()
      ? sortOrder
      : -sortOrder,
  );
};

export default function UserCurrentProductsTable({
  list = [],
  isLoading,
  accessibleAgreements,
  user,
  onAddProduct,
}: UserCurrentProductsTableProps) {
  const {
    hasAccess,
    isLoading: isAccessLoading,
    restrictedAgreementIds: restrictedAgreementIdsList,
  } = usePermissions();
  const noData = !isLoading && list?.length < 1;
  const [products, setProducts] = useState<UserProduct[]>(list);
  const [productAction, setProductAction] = useState<
    'remove' | 'unenroll' | 'diagnose' | 'reassign' | null
  >(null);
  const [selectedProduct, setSelectedProduct] =
    useState<ProductApiModel | null>(null);
  const [selectedUserProductId, setSelectedUserProductId] = useState<
    number | null
  >(null);
  const [selectedAgreementId, setSelectedAgreementId] = useState<string | null>(
    null,
  );
  const productList = useMemo<{ [name: string]: ProductApiModel }>(() => {
    const temp: {} = {};
    accessibleAgreements.map((d: AgreementApiModel) => {
      d.products.map((p: ProductApiModel) => {
        temp[p.accountProductId] = p;
        return p;
      });
      return d;
    });
    return temp;
  }, [accessibleAgreements]);
  const onSelectProductAction = (
    action: 'remove' | 'unenroll' | 'diagnose' | 'reassign' | null,
    userProductId: number,
    accountProductId: number,
    agreementId: string,
  ) => {
    const p = productList[accountProductId];
    if (p !== undefined && p) {
      setSelectedUserProductId(userProductId);
      setSelectedProduct(p);
      setProductAction(action);
      setSelectedAgreementId(agreementId);
      return;
    }
    setSelectedProduct(null);
    setSelectedUserProductId(null);
    setSelectedAgreementId(null);
    setProductAction(null);
  };

  const unSelectProduct = () => {
    setSelectedProduct(null);
    setSelectedAgreementId(null);
    setProductAction(null);
  };
  const hasRestrictedProduct = useMemo<boolean>(
    () =>
      products.some(
        (p) =>
          p.enrollmentStatus &&
          p.enrollmentStatus.toLowerCase() === 'restricted',
      ),
    [products],
  );

  const restrictedAgreementIds = useMemo<Set<string>>(
    () => new Set(restrictedAgreementIdsList),
    [restrictedAgreementIdsList],
  );

  const columns = useMemo(
    () =>
      getColumns(
        hasAccess({ permissions: [PERMISSIONS.ManageDealUsers] }),
        hasAccess({ permissions: [PERMISSIONS.ManageElevatedAdmins] }),
        hasAccess({ permissions: [PERMISSIONS.UnassignProducts] }),
        {
          onUnEnroll: (
            userProductId: number,
            accountProductId: number,
            agreementId: string | null,
          ) => {
            onSelectProductAction(
              'unenroll',
              userProductId,
              accountProductId,
              agreementId,
            );
          },
          onRemove: (
            userProductId: number,
            accountProductId: number,
            agreementId: string | null,
          ) => {
            onSelectProductAction(
              'remove',
              userProductId,
              accountProductId,
              agreementId,
            );
          },
          onDiagnose: (
            userProductId: number,
            accountProductId: number,
            agreementId: string | null,
          ) => {
            onSelectProductAction(
              'diagnose',
              userProductId,
              accountProductId,
              agreementId,
            );
          },
          onReassign: (
            userProductId: number,
            accountProductId: number,
            agreementId: string | null,
          ) => {
            onSelectProductAction(
              'reassign',
              userProductId,
              accountProductId,
              agreementId,
            );
          },
        },
        user.isRemoved,
        hasRestrictedProduct,
        user.invitationStatus?.toLowerCase() === 'pending',
        restrictedAgreementIds,
      ),
    [isLoading, accessibleAgreements, isAccessLoading, restrictedAgreementIds],
  );
  const [sortedColumn, setSortedColumn] = useState<SortedColumn>(null);
  const handleSort = (column: SortedColumn) => {
    setSortedColumn(column);
  };

  useEffect(() => {
    if (sortedColumn) {
      const sortOrder = sortedColumn?.direction === 'Asc' ? 1 : -1;
      setProducts(sortProducts(products, sortedColumn.name, sortOrder));
    }
  }, [sortedColumn]);

  useEffect(() => {
    if (list.length > 0) {
      setProducts(list);
    }
  }, [list, isLoading]);

  return (
    <div>
      {hasRestrictedProduct && !user.isRemoved && (
        <Alert
          icon={AlertErrorIcon}
          variant="danger"
          alertText={
            "This agreement includes products that are not available in the assigned user's region. To resolve this, select the action to reassign each restricted product to an eligible agreement."
          }
        />
      )}
      {user.invitationStatus?.toLowerCase() === 'pending' && (
        <Alert
          alertText="User's platform registration is still pending."
          icon={TriangleAlertIcon}
          variant="warning"
        />
      )}
      <div className="d-flex align-items-center w-100">
        <div className="d-flex align-items-center flex-wrap">
          <h5 className="mb-0 me-1"> Products</h5>
          <ChipMetadata
            variant="gray"
            text={` ${user.isRemoved ? 0 : list?.length || 0} product${
              list?.length > 1 ? 's' : ''
            }`}
          />
        </div>
        <Button
          size="sm"
          disabled={user?.isRemoved}
          className="mt-2 ms-auto"
          titleText="Add Products"
          onClick={() => onAddProduct(user)}
          icon={PlusIcon}
        />
      </div>
      <p className="mb-2">
        These are the products that are currently assigned to this person’s
        account.
      </p>
      <div
        className={`${styles.usersTableContainer} thead-bg-gray`}
        data-testid="user-list"
      >
        {!isLoading && !user.isRemoved && (
          <ResponsiveTable
            data={products}
            columns={columns}
            onSort={handleSort}
            className={`${userProductStyles.tableUsers} ${
              hasRestrictedProduct ? styles.hasRestrictedProducts : ''
            }`}
          />
        )}
        {(noData || user.isRemoved) && (
          <div
            className={`d-block my-5 text-center mx-auto w-75 ${styles.noResult}`}
          >
            No Data to Display...
          </div>
        )}
        {isLoading && <p className="py-2">Loading...</p>}
      </div>
      {productAction === 'diagnose' && selectedProduct && (
        <DiagnosticsModal
          user={user}
          diagnosableProduct={selectedProduct}
          onCancel={() => unSelectProduct()}
        />
      )}
      {productAction === 'unenroll' && selectedProduct && (
        <UnErrollProductModal
          onCancel={() => unSelectProduct()}
          agreementId={selectedAgreementId}
          product={selectedProduct}
          user={user}
          userProductId={selectedUserProductId}
        />
      )}
      {productAction === 'remove' && selectedProduct && (
        <RemoveProductModal
          onCancel={() => unSelectProduct()}
          agreementId={selectedAgreementId}
          product={selectedProduct}
          user={user}
          userProductId={selectedUserProductId}
        />
      )}
      {productAction === 'reassign' && selectedProduct && (
        <ReassignProducts
          toRemoveProduct={selectedProduct}
          onCancel={() => unSelectProduct()}
          user={user}
          agreements={accessibleAgreements}
          currentProductAgreement={
            products.find((p) => p.id === selectedUserProductId)?.agreementId
          }
        />
      )}
    </div>
  );
}
